import { useEffect, useState } from "react";
import {
  BodyComponent,
  HeaderComponent,
  SaveButton,
  SelectResourceList,
  SelectWithSearch,
  trans,
} from "@comps/components";
import { Alert, Card, Col, Form, Row, Skeleton, Space, Typography, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate, useParams } from "react-router-dom";
import { createSelectList } from "@comps/commonFunctions";
import { makeRequest } from "@utils/helpers";
import { Const } from "../../constants";
import { getDashboardDirectivesReq, getDashboardTypes, getDataByIdReq, updateDataReq } from "../requests";
import CustomDashboardBuilder from "./CustomDashboardBuilder";
import {
  applyLayoutToRow,
  clearWidgetFromRows,
  createEmptyRow,
  getColumnLength,
  getWidthFromLength,
  moveRowPosition,
} from "./customDashboardBuilderUtils";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { workflowTooltips } from "../../common/workflowTooltips";

const { Paragraph, Text, Title } = Typography;

const langKey = Const.customDashboard.lngKey;

const fieldLabel = makeFieldLabel(workflowTooltips.customDashboard, { namespace: "customDashboard", label: (key) => trans(`${langKey}.${key}`) });
const formName = "updateCustomDashboard";

const pageConfig = {
  headers: {
    title: trans(`${langKey}.moduleName`),
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: Const.customDashboard.route,
      },
    ],
  },
};

const sectionCardStyle = {
  borderRadius: 16,
  border: "1px solid #d7e3f4",
  boxShadow: "0 10px 28px rgba(20, 54, 96, 0.08)",
};

const UpdateCustomDashboard = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loader, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardTypes, setDashboardTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [forEmployment, setForEmployment] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [previousData, setPreviousData] = useState(null);
  const [deletedRows, setDeletedRows] = useState([]);
  const [rows, setRows] = useState([createEmptyRow()]);

  useEffect(() => {
    getRelatedData();
    getDataById();
    // eslint-disable-next-line
  }, []);

  const getDataById = () => {
    makeRequest(setLoader, getDataByIdReq, id, onGetData, onErrorGettingData);
  };

  const onGetData = (response) => {
    if (response.custom_dashboard) {
      const dashboardRows = transformPayloadToState(response.custom_dashboard);
      setRows(dashboardRows.length > 0 ? dashboardRows : [createEmptyRow()]);
      form.setFieldsValue(response.custom_dashboard);
      setForEmployment(!!response.custom_dashboard.for_employment);
      setPreviousData(response.custom_dashboard);
    }
  };

  const onErrorGettingData = (error, res) => {
    message.error(res?.response?.data?.message || "Unable to load dashboard details.");
    setLoading(false);
  };

  const getRelatedData = () => {
    makeRequest(setLoader, getDashboardDirectivesReq, null, onGetDirectivesSuccess, onGetDirectiveError);
    makeRequest(setLoader, getDashboardTypes, null, onGetDashboardTypes, onGetDashboardTypesError);
  };

  const onGetDirectivesSuccess = (response) => {
    setRoles(createSelectList(response.roles));
    setWidgets(response.dashboard_directives || []);
    setLoading(false);
  };

  const onGetDashboardTypes = (response) => {
    const result = Object.values(response.resources.data).map((item) => ({
      label: item.name,
      value: item.id,
    }));
    setDashboardTypes(result);
    setLoading(false);
  };

  const onGetDashboardTypesError = () => {
    setLoading(false);
  };

  const onGetDirectiveError = (err, res) => {
    message.error(res?.response?.data?.message || "Unable to load widget directives.");
    setLoading(false);
  };

  const transformPayloadToState = (payload) => payload.rows.map((row) => ({
    row_state: "Updated",
    id: row.id,
    columns: row.columns.map((column) => ({
      row_state: "Updated",
      id: column.id,
      width: getWidthFromLength(column.length),
      widgets: column.dashboard_directive_id ? [column.dashboard_directive_id] : [],
    })),
  }));

  const formatRowsForPayload = (builderRows) => ({
    rows: builderRows.map((row, rowIndex) => ({
      id: row.id,
      order: rowIndex,
      row_state: row.row_state,
      columns: row.columns.map((column) => ({
        id: column.id,
        row_state: row.row_state,
        length: getColumnLength(column.width),
        dashboard_directive_id: column.widgets.length > 0 ? column.widgets[0] : null,
      })),
    })),
  });

  const onSubmit = (data) => {
    const dashboardRows = formatRowsForPayload(rows);
    dashboardRows.rows = dashboardRows.rows.concat(deletedRows);

    const payload = {
      id,
      custom_dashboard: {
        id,
        employment_id: forEmployment ? data.employment_id : null,
        role_id: !forEmployment ? data.role_id : null,
        for_employment: forEmployment,
        type: data.type,
        rows: dashboardRows.rows,
        dashboard_rows: dashboardRows.rows,
      },
    };

    makeRequest(setLoader, updateDataReq, payload, onSuccess, onError);
    setSaving(true);
  };

  const onSuccess = () => {
    message.success("Saved successfully");
    goBack();
  };

  const onError = (err, res) => {
    setSaving(false);
    message.error(res?.response?.data?.message || "Unable to update custom dashboard.");
  };

  const goBack = () => {
    navigate("/workflowManagement/customDashboard/index");
  };

  const handleMoveRow = (index, direction) => {
    setRows((prevRows) => moveRowPosition(prevRows, index, direction));
  };

  const handleRemoveRow = (indexToRemove) => {
    const rowToRemove = rows[indexToRemove];
    const existingRow = rowToRemove?.id ? previousData?.rows?.find((item) => item.id === rowToRemove.id) : null;

    if (existingRow?.id !== undefined) {
      const deletedRow = {
        ...existingRow,
        row_state: "Deleted",
        columns: (existingRow.columns || []).map((column) => ({
          ...column,
          row_state: "Deleted",
        })),
      };

      setDeletedRows((prevRows) => [...prevRows, deletedRow]);
    }

    setRows((prevRows) => prevRows.filter((_, index) => index !== indexToRemove));
  };

  const handleAddRow = () => {
    setRows((prevRows) => [...prevRows, { ...createEmptyRow(), row_state: "Added" }]);
  };

  const handleClearWidget = (rowIndex, colIndex) => {
    setRows((prevRows) => clearWidgetFromRows(prevRows, rowIndex, colIndex));
  };

  const handleSplitColumn = (rowIndex, colIndex, widths) => {
    setRows((prevRows) => {
      const nextRows = applyLayoutToRow(prevRows, rowIndex, colIndex, widths).map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }

        return {
          ...row,
          row_state: row.row_state || "Updated",
          columns: row.columns.map((column) => ({
            ...column,
            row_state: column.row_state || "Updated",
          })),
        };
      });

      if (nextRows[rowIndex]?.columns.length === prevRows[rowIndex]?.columns.length && widths.length > 1) {
        message.warning("Cannot split the column. The row can have a maximum of 4 columns.");
      }

      return nextRows;
    });
  };

  return (
    <BodyComponent>
      <HeaderComponent headers={ pageConfig.headers } modkey={ props.modkey }>

        <SaveButton text="Back" icon={ <ArrowLeftOutlined /> } onClick={ goBack } />
      </HeaderComponent>

      <Skeleton loading={ loading }>
        <Space direction="vertical" size={ 24 } style={ { width: "100%" } }>
          <Card style={ sectionCardStyle } bodyStyle={ { padding: 20 } }>
            <Space direction="vertical" size={ 18 } style={ { width: "100%" } }>
              <div>
                <Title level={ 4 } style={ { marginBottom: 4, color: "#20324b" } }>
                  { props.disabled ? "Dashboard Overview" : "Dashboard Setup" }
                </Title>
                <Paragraph type="secondary" style={ { marginBottom: 0 } }>
                  { props.disabled
                    ? "Review the audience, dashboard type, and configured widget layout for this custom dashboard."
                    : "Update the dashboard audience or type, then adjust rows and widgets in the layout workspace below." }
                </Paragraph>
              </div>

              {/* <Alert
                type={ props.disabled ? "warning" : "info" }
                showIcon
                message={ props.disabled ? "Read-only mode" : "Builder workflow" }
                description={ props.disabled
                  ? "Editing actions are hidden in view mode so the current configuration stays safe to review."
                  : "Select a slot in the layout first, then drag a widget into it or use the Add button in the widget library for quick placement." }
              /> */}

              <Form form={ form } name={ formName } layout="vertical" onFinish={ onSubmit } disabled={ props.disabled }>
                <Row gutter={ [16, 0] }>
                  <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item name="for_employment" label={ fieldLabel("for_employment", "Audience") } rules={ rules.for_employment }>
                      <SelectWithSearch
                        options={ [
                          {
                            label: "Role",
                            value: false,
                          },
                          {
                            label: "Employee",
                            value: true,
                          },
                        ] }
                        onChange={ (value) => setForEmployment(value) }
                        placeholder="Select audience"
                      />
                    </Form.Item>
                  </Col>

                  { forEmployment ? (
                    <Col lg={ 8 } md={ 12 } xs={ 24 }>
                      <Form.Item name="employment_id" label={ fieldLabel("employment_id", "Employee") } rules={ rules.employment_id }>
                        <SelectResourceList type="employment" placeholder="Search employee" />
                      </Form.Item>
                    </Col>
                  ) : (
                    <Col lg={ 8 } md={ 12 } xs={ 24 }>
                      <Form.Item name="role_id" label={ fieldLabel("role_id", "Role") } rules={ rules.role_id }>
                        <SelectWithSearch options={ roles } placeholder="Select role" />
                      </Form.Item>
                    </Col>
                  ) }

                  <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item name="type" label={ fieldLabel("type", "Dashboard Type") } rules={ rules.dashboard_type }>
                      <SelectWithSearch options={ dashboardTypes } placeholder={ trans(`${langKey}.type`) } />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Space>
          </Card>
          { !props.disabled && (
            <SaveButton text="Publish Changes" key="update_button" htmlType="submit" form={ formName } loading={ saving || loader } />
          ) }
          <DndProvider backend={ HTML5Backend }>
            <CustomDashboardBuilder
              widgets={ widgets }
              rows={ rows }
              disabled={ props.disabled }
              onChangeRows={ setRows }
              onAddRow={ handleAddRow }
              onMoveRow={ handleMoveRow }
              onRemoveRow={ handleRemoveRow }
              onClearWidget={ handleClearWidget }
              onSplitColumn={ handleSplitColumn }
            />
          </DndProvider>

          <Text type="secondary">
            Each slot can hold one widget. Moving or reassigning a widget removes it from its previous slot automatically.
          </Text>
        </Space>
      </Skeleton>
    </BodyComponent>
  );
};

export default UpdateCustomDashboard;

const rules = {
  role_id: [
    { required: true, message: "Role is required" },
  ],
  employment_id: [
    { required: true, message: "Employee is required" },
  ],
  dashboard_type: [
    { required: true, message: trans(`${langKey}.dashboard_type`) },
  ],
  for_employment: [
    { required: true, message: "Select whether this dashboard is for a role or an employee." },
  ],
};
